import { Moon, Play, Sun } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useAuthActions } from '@creditornot/wolt-auth/react';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import styled from 'styled-components';

import { fetchMe } from 'apis/restaurant-api/merchant-admin/user/api';
import { ConfirmModal } from 'common/components';
import Send from 'common/components/icons/Send';
import { useThemeMode } from 'common/themeMode';
import { FeatureType } from 'common/types';
import { PWAContext } from 'core/pwa/PWAContext';
import { telemetryClickInstallFromUserDropDown, telemetryClickIOSInstructions } from 'core/pwa/telemetry';
import { openIntercom } from 'modules/intercom';
import { VenueNps } from 'modules/nps/features/VenueNps';
import useIsNpsFlagEnabled from 'modules/nps/useIsNpsFlagEnabled';

import LanguageSelectorMenuItemGroup from './LanguageSelectorMenuItemGroup';
import HeaderUserBadge from '../components/HeaderUserBadge';

const MenuRoot = styled.div`
  max-height: calc(100vh - 50px);
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  padding: 12px 16px;
  &:not(:first-child) {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const StyledLanguageSelectorMenuItem = styled(LanguageSelectorMenuItemGroup)`
  padding: 12px 16px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const UserTitle = styled.h3`
  font-size: 15px;
  font-weight: 500;
  color: ${cbSemanticCssVars.textPrimary};
  margin-block-end: 0;
`;

const SendIcon = styled(Send)`
  fill: ${cbSemanticCssVars.textPrimary};
`;

type Params = {
  feature?: FeatureType;
  slug?: string;
};

const useLogout = (): [boolean, () => void] => {
  const { logout } = useAuthActions();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const doLogout = () => {
    setIsLoggingOut(true);
    logout();
  };

  return [isLoggingOut, doLogout];
};

const UserDropdown = () => {
  const { themeMode, setThemeMode } = useThemeMode();
  const { shouldShowInstallGeneralPrompt, shouldShowInstallIOSPrompt, setIsInstructionsModalOpen, install } =
    useContext(PWAContext);

  const [show, setShow] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, doLogout] = useLogout();

  const { data: user } = useQuery(['/v1/user/me'], () => fetchMe(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const handleTriggerClick = () => setShow(!show);
  const handleClickOutside = () => setShow(false);
  const handleSelectLanguage = () => setShow(false);

  const [showNps, setShowNps] = useState(false);
  const isNpsFlagEnabled = useIsNpsFlagEnabled();
  const params = useParams<Params>();

  const isValidFeatureForNps = params.feature === 'venue';
  return (
    <>
      <LegacyPopover
        content={
          <MenuRoot>
            <StyledMenuItemGroup>
              <UserTitle>{user?.email || ''}</UserTitle>
            </StyledMenuItemGroup>
            <StyledLanguageSelectorMenuItem onChange={handleSelectLanguage} />
            <StyledMenuItemGroup>
              <MenuActionItem
                variant="black"
                icon={themeMode === 'light' ? <Moon /> : <Sun />}
                onClick={() => setThemeMode(themeMode === 'dark' ? 'light' : 'dark')}
              >
                <FormattedMessage id={themeMode === 'light' ? 'navigation.switch-dark' : 'navigation.switch-light'} />
              </MenuActionItem>
              {isNpsFlagEnabled && isValidFeatureForNps && (
                <MenuActionItem variant="black" icon={<SendIcon />} onClick={() => setShowNps(true)}>
                  <FormattedMessage id="navigation.give-feedback" />
                </MenuActionItem>
              )}
              {shouldShowInstallGeneralPrompt ||
                (shouldShowInstallIOSPrompt && (
                  <MenuActionItem
                    variant="black"
                    icon={<Play />}
                    onClick={() => {
                      if (shouldShowInstallIOSPrompt) {
                        telemetryClickIOSInstructions();
                        setIsInstructionsModalOpen(true);
                      }

                      if (shouldShowInstallGeneralPrompt) {
                        telemetryClickInstallFromUserDropDown();
                        install();
                      }
                    }}
                  >
                    <FormattedMessage id="navigation.install-app" />
                  </MenuActionItem>
                ))}
              <MenuActionItem variant="black" onClick={openIntercom}>
                <FormattedMessage id="navigation.support" />
              </MenuActionItem>
              <MenuActionItem variant="black" onClick={() => setIsLogoutModalOpen(true)}>
                <FormattedMessage id="navigation.logout" />
              </MenuActionItem>
            </StyledMenuItemGroup>
          </MenuRoot>
        }
        onClickOutside={handleClickOutside}
        show={show}
        placement="right-end"
        renderArrow={false}
        disableContentPadding
      >
        <HeaderUserBadge onClick={handleTriggerClick} user={user} />
      </LegacyPopover>
      <ConfirmModal
        header={<FormattedMessage id="logout.header" />}
        description={<FormattedMessage id="logout.description" values={{ user: user?.email }} />}
        cancelText={<FormattedMessage id="logout.cancel" />}
        confirmText={<FormattedMessage id="logout.sign-out" />}
        onCancel={() => setIsLogoutModalOpen(false)}
        onConfirm={doLogout}
        open={isLogoutModalOpen}
        loading={isLoggingOut}
      />
      {showNps && isValidFeatureForNps && <VenueNps manual onClose={() => setShowNps(false)} />}
    </>
  );
};

export default UserDropdown;
